import { Link } from 'react-router-dom';

import { Badge } from '../common';
import { formatMs } from '../../utils/format';
import { formatSimilarity } from './ImageResults';
import './search.css';

const SOURCE_LABEL = { message: 'conversation', job_item: 'batch job' };

/**
 * Codings the model has produced, from anywhere: chat, batch and /predict alike.
 *
 * A row links back to where the coding lives rather than rendering the whole
 * attribute table inline. Two reasons: the inspector already does that job, and
 * twenty expanded tables is a page nobody can scan. What the row shows instead is
 * the part that matched, plus enough of the parsed values to recognise the coding.
 */
export function AnalysisResults({ results, meta }) {
  return (
    <>
      <div className="results__bar">
        <span className="results__count">
          <b>{meta?.count ?? results.length}</b> coding
          {(meta?.count ?? results.length) === 1 ? '' : 's'}
          {meta?.elapsedMs !== undefined ? ` · ${formatMs(meta.elapsedMs)}` : ''}
        </span>
      </div>

      <div className="srows">
        {results.map((hit) => (
          <AnalysisRow key={hit.analysis_id} hit={hit} />
        ))}
      </div>
    </>
  );
}

function AnalysisRow({ hit }) {
  const values = Object.entries(hit.values || {}).slice(0, 4);
  const remaining = Object.keys(hit.values || {}).length - values.length;

  return (
    <article className="srow">
      <div className="srow__head">
        <span className="srow__score" title="Cosine similarity, not a confidence">
          {formatSimilarity(hit.similarity)}
        </span>
        <Badge>{SOURCE_LABEL[hit.source] || hit.source || 'predict'}</Badge>
        {hit.version_id && <Badge tone="accent">{hit.version_id}</Badge>}
        {hit.mode && hit.mode !== 'extraction' && <Badge>{hit.mode}</Badge>}
        <Destination hit={hit} />
      </div>

      {/* Whitespace preserved: an extraction answer is one numbered line per
          attribute, and reflowing it into a paragraph makes it unreadable. */}
      <p className="srow__text">{hit.text}</p>

      {values.length > 0 && (
        <div className="schips">
          {values.map(([name, value]) => (
            <span key={name} className="schip" title={`${name}: ${value}`}>
              <span className="schip__key">{name}</span>
              {value}
            </span>
          ))}
          {remaining > 0 && <span className="schip schip--more">+{remaining} more</span>}
        </div>
      )}
    </article>
  );
}

/**
 * Where this coding came from, as a link where one exists.
 *
 * A ``/predict`` result has no conversation and no job unless the caller asked for
 * one, so there is genuinely nowhere to send you — saying so is better than a dead
 * link that looks like a bug.
 */
function Destination({ hit }) {
  if (hit.conversation_id) {
    return (
      <Link className="srow__open" to={`/c/${hit.conversation_id}`}>
        Open conversation →
      </Link>
    );
  }
  if (hit.job_id) {
    return (
      <Link className="srow__open" to={`/batch?job=${hit.job_id}`}>
        Open batch job →
      </Link>
    );
  }
  return <span className="srow__open srow__open--none">API result · not stored in a thread</span>;
}

/**
 * Coding guidance, quoted with its source.
 *
 * The citation is the point of this collection: an answer that names the rule
 * behind a value is worth more than one that asserts the value. So the document,
 * section and page are given the same weight as the text.
 */
export function ManualResults({ results, meta }) {
  return (
    <>
      <div className="results__bar">
        <span className="results__count">
          <b>{meta?.count ?? results.length}</b> passage
          {(meta?.count ?? results.length) === 1 ? '' : 's'}
          {meta?.elapsedMs !== undefined ? ` · ${formatMs(meta.elapsedMs)}` : ''}
        </span>
      </div>

      <div className="srows">
        {results.map((hit) => (
          <article className="srow" key={hit.chunk_id}>
            <div className="srow__head">
              <span className="srow__score">{formatSimilarity(hit.similarity)}</span>
              {hit.attribute && <Badge tone="accent">{hit.attribute}</Badge>}
            </div>
            <blockquote className="scite">
              <p className="srow__text">{hit.text}</p>
              <cite className="scite__where">{citation(hit)}</cite>
            </blockquote>
          </article>
        ))}
      </div>
    </>
  );
}

function citation(hit) {
  return [hit.document, hit.section, hit.page ? `p. ${hit.page}` : null]
    .filter(Boolean)
    .join(' · ');
}
